import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { filter, map } from 'rxjs/operators';
//import { GridOptions, ColDef } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from 'ag-grid-community';


import { RouterLinkRendererComponent } from '../ag-grid/router-link-renderer.component';

import { DialogService } from "../../../common/services/dialog.service";
import { FinancialAssuranceService } from '../../services/financial-assurance.service';
import { GridFormatterService } from '../../../common/services/grid-formatter.service';
import { LoaderService } from '../../services/loader.service';
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { InsurancePolicy } from '../../models/financialAssurance.model';
import { dateComparator } from '../data-table/comparators/data-table.comparators';
import { IBooleanFilterParams, AgGridBooleanFilterComponent } from '../ag-grid/filters/boolean-filter.component';
import { AuthService } from '../../../services/auth.service';
import { LunaClaimTypes, LunaClaimValues } from '../../../models/user-identity.model';
import { OrganizationComplianceDetails } from "../../models/organization.model";


@Component({
    selector: 'fa-insurance',
    templateUrl: './fa-insurance.component.html',
    standalone: false
})
export class FinancialAssuranceInsuranceComponent implements OnInit, OnDestroy {

    @Input() public externalView: boolean = true;
    @Input() public orgId: string;
    _isRecalculating: boolean = false;

    get isRecalculating(): boolean {
        return this._isRecalculating;
    }

    @Input() set isRecalculating(value: boolean) {
        this._isRecalculating = value;
    }

    _isLoading: boolean = true;

    get isLoading(): boolean {
        return this._isLoading;
    }

    @Input() set isLoading(value: boolean) {
        this._isLoading = value;
    }

    @Output() recalculateEvent: EventEmitter<string> = new EventEmitter<string>();


    get activeInsuranceAmount(): string {
        if (this.isLoading) return "Loading...";
        if (this.isRecalculating) return "Calculating...";
        if (this.complianceDetails != null && this.complianceDetails.insuranceProvided != null) return this.currencyPipe.transform(this.complianceDetails.insuranceProvided);
        return "Unavailable";
    }

    get requiredInsuranceAmount(): string {
        if (this.isLoading) return "Loading...";
        if (this.isRecalculating) return "Calculating...";
        if (this.complianceDetails != null && this.complianceDetails.requiredInsurance != null) return this.currencyPipe.transform(this.complianceDetails.requiredInsurance);
        return "Unavailable";
    }


    public insuranceDecisionBasis: string;

    private _complianceDetails: OrganizationComplianceDetails;

    get complianceDetails(): OrganizationComplianceDetails {
        return this._complianceDetails;
    }

    @Input() gridAriaLabel: string;
    @Input() set complianceDetails(summary: OrganizationComplianceDetails) {
        this._complianceDetails = summary;
        if (summary != null) {
            this.insuranceDecisionBasis = (summary.insuranceDecisionBasis != null)
                ? summary.insuranceDecisionBasis.join("<br />")
                : "";
        } else {
            this.insuranceDecisionBasis = "";
        }
    }


    
    public subscriptions: Subscription = new Subscription();

    public colAgent: ColDef = {
        headerClass: "wrapped",
        headerName: 'Agent',
        headerTooltip: 'Agent',
        field: 'agent',
        tooltipField: 'agent'
    };

    public colType: ColDef = {
        headerClass: "wrapped",
        headerName: 'Type',
        headerTooltip: 'Type',
        field: 'type',
        tooltipField: 'type',
        minWidth: 120,
        maxWidth: 120
    };

    public colAmount: ColDef = {
        headerClass: "wrapped",
        headerName: 'Amount',
        headerTooltip: 'Amount',
        field: "limit",
        filter: "agNumberColumnFilter",
        filterParams: this.formatterService.defaultNumberFilterParams,
        tooltipValueGetter: this.formatterService.formatCurrency('limit'),
        valueFormatter: this.formatterService.formatCurrency('limit'),
        minWidth: 120,
        maxWidth: 120
    };

    public colEnvironmentalEndorsement: ColDef = {
        headerClass: "wrapped",
        headerName: 'Environmental Endorsement',
        headerTooltip: 'Environmental Endorsement',
        cellStyle: { 'white-space': 'normal' },
        tooltipValueGetter: this.checkEnvironmentalEndorsementFlag(),
        valueFormatter: this.checkEnvironmentalEndorsementFlag(),
        valueGetter: this.hasEnvironmentalEndorsement(),
        field: "hasEnvironmentalEndorsement",
        filter: AgGridBooleanFilterComponent,
        filterParams: {
            isInServerSideFilterMode: false,
        } as IBooleanFilterParams,
        minWidth: 145,
        maxWidth: 145,
    };

    public colEffective: ColDef = {
        headerClass: "wrapped",
        headerName: 'Effective',
        headerTooltip: 'Effective',
        field: "effectiveDate",
		tooltipValueGetter: this.formatterService.formatDate("effectiveDate", "MM/dd/yyyy", "N/A", "+0000"),
		valueFormatter: this.formatterService.formatDate("effectiveDate", "MM/dd/yyyy", "N/A", "+0000"),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 125,
    };

    public colExpires: ColDef = {
        headerClass: "wrapped",
        headerName: 'Expires',
        headerTooltip: 'Expires',
        field: "expirationDate",
		tooltipValueGetter: this.formatterService.formatDate("expirationDate", "MM/dd/yyyy", "N/A", "+0000"),
		valueFormatter: this.formatterService.formatDate("expirationDate", "MM/dd/yyyy", "N/A", "+0000"),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 125,
        resizable: false
    };

    public colInsurer: ColDef = {
        headerClass: "wrapped",
        headerName: 'Insurer',
        headerTooltip: 'Insurer',
        field: 'insurer',
        tooltipField: 'insurer',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        minWidth: 100
    };

    public colInsurerLinked: ColDef = {
        headerClass: "wrapped",
        headerName: 'Insurer',
        headerTooltip: 'Insurer',
        tooltipField: 'insurer',
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            idField: 'agencyId',
            url: '/admin/insurance',
            textField: 'insurer'
            },
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        minWidth: 100
    };

	public gridOptions: GridOptions;
	private gridApi?: GridApi;
    get isCompliant(): boolean {
        if (this.complianceDetails == null) return false;
        return this.complianceDetails.isInsuranceCompliant;
    }
    public orgName: string = '';
    public policies: InsurancePolicy[];

    public externalColumnDefs = [this.colInsurer, this.colType, this.colEnvironmentalEndorsement, this.colEffective, this.colExpires, this.colAmount];
    public internalColumnDefs = [this.colInsurerLinked, this.colType, this.colEnvironmentalEndorsement, this.colEffective, this.colExpires, this.colAmount];

    public hasAddInsurancePermissions: boolean = false;

    constructor(
        public financialAssuranceService: FinancialAssuranceService,
        public domainService: DomainDefinitionService,
        public currencyPipe: CurrencyPipe,
        public datePipe: DatePipe,
        public router: Router,
        private formatterService: GridFormatterService,
        public dialogService: DialogService,
        public loader: LoaderService,
        public authService: AuthService) {
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    ngOnInit() {
        this.hasAddInsurancePermissions = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.CanAddBonding).length > 0;
        let ins$ = this.financialAssuranceService.getInsurances(this.orgId);
        let domain$ = this.domainService.getValues("INS_TYPE");

        let data$ = forkJoin([ins$, domain$]).pipe(map(([data, domains]) => {
            let expiredPolicies: InsurancePolicy[] = [];
            let now = new Date();
            let policies: InsurancePolicy[] = [];
            data.filter(d => d.policies != null).forEach(ins => {
                ins.policies.forEach(policy => {
                    let domain = domains.find(d => d.Code.toLocaleLowerCase() === policy.type.toLocaleLowerCase()
                        || d.Code.toLocaleLowerCase().indexOf(policy.type.toLocaleLowerCase()) !== -1);
                    if (domain != null) {
                        policy.type = domain.Definition;
                        policy.type = policy.type.replace("Liability", "");
                        policy.type = policy.type.replace("(each occurrence)", "");
                        policy.type = policy.type.trim();
                    }
                    policy.agencyId = ins.id;
					policy.agent = ins.agent;
					if ((policy.expirationDate != null && new Date(policy.expirationDate) < now) || (policy.releasedDate != null && new Date(policy.releasedDate) < now) || policy.replacedBy != null) {
						expiredPolicies.push(policy);
					}
					else {
						policies.push(policy);
					}
  
                });
            });
            expiredPolicies = expiredPolicies.sort((a, b) => {
                if (a.effectiveDate < b.effectiveDate) return 1;
                if (a.effectiveDate > b.effectiveDate) return -1;
                return 0;
            });
            policies = policies.sort((a, b) => {
                if (a.effectiveDate < b.effectiveDate) return 1;
                if (a.effectiveDate > b.effectiveDate) return -1;
                return 0;
			});
			let retVal = policies.concat(expiredPolicies);
            return policies.concat(expiredPolicies);
        })).subscribe(
            (policies) => { this.policies = policies; },
            (error) => { console.error(error); }
        )

        this.subscriptions.add(data$);

        this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.externalView ? this.externalColumnDefs : this.internalColumnDefs);
        this.gridOptions.getRowClass = (params: any) => {
            if (!this.isInsuranceActive(params.data)) return 'red';
        };
        this.gridOptions.headerHeight = 45;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    public addInsurance(): void {
        this.router.navigateByUrl('/flow/create/nGTt2RanGTCqd12XYS');
    }

    public canAddInsurance(): boolean {
        return this.hasAddInsurancePermissions;
    }

    public hasEnvironmentalEndorsement(): any {
        return (obj): boolean => {
            if (obj.data) {
                let hasEnvironmentalEndorsement = obj.data["hasEnvironmentalEndorsement"];
                let insuranceType = obj.data["type"];

                if (insuranceType == "Environmental")
                    return true;
                else
                    return hasEnvironmentalEndorsement;
            }
        }
    }

    public checkEnvironmentalEndorsementFlag(): any {
        return (obj) => {
            if (obj.data) {
                var path = "$.hasEnvironmentalEndorsement";
                let fieldValue = this.formatterService.tryGetValue(obj.data, path);

                let insuranceType = obj.data["type"]

                if (insuranceType == "Environmental")
                    return 'Yes';

                if (fieldValue != undefined && fieldValue != null)
                    return fieldValue ? 'Yes' : 'No';
                else return "N/A";
            }
        }
    }

    public isInsuranceActive(data: InsurancePolicy): boolean {
        const now = new Date();

        if (!data.isActive) return false;

        if (data.expirationDate) {
            const expirationDate = new Date(data.expirationDate);
            if (expirationDate < now) return false;
        };

        return true;
    }

    recalculate(): void {
        this.insuranceDecisionBasis = "";
        this.recalculateEvent.emit(this.orgId);
    }

    public resizeColumns(): void {
        if (this.gridApi)
            setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
    }

}
